/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useState, useCallback, useMemo } from 'react';
import { Box, Container, Stack, Typography, Button } from '@mui/material';
import PositiveButton from 'components/cmn/PositiveButton';
import useProjectSurveyInfo from 'hooks/useProjectSurveyInfo';
import { useProject } from 'hooks/useProject';
import SurveyRadioBox from 'components/inspection/survey/SurveyRadioBox';
import { IPostSurveyResultListProps } from 'interface/project/projectInfo';
import ProgressIndicator from 'components/inspection/player/ProgressIndicator';
import SurveyShortAnswer from 'components/inspection/survey/SurveyShortAnswer';
import SurveyMultiChoice from 'components/inspection/survey/SurveyMultiChoice';
import SurveyDescriptionAnswer from 'components/inspection/survey/SurveyDescriptionAnswer';
import ClassificationGuideComplete from 'components/inspection/ClassificationGuideComplete';
import SurveyLK from 'components/inspection/survey/SurveyLK';
import { useAppDispatch } from 'store';
import { postSurveyResult } from 'store/project/projectSlice';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import { showMessage } from 'store/message/messageSlice';
import ConfirmDialog from 'components/cmn/ConfirmDialog';
import useLogin from 'hooks/useLogin';
import axios from 'axios';
import { makeHeader } from 'libs/mindUtils';
import { SK_LOGO } from 'constants/images';

function SurveyPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const {
    progressInfo,
    changeProgressInfo,
    changeSurveyInfoResult,
    changeSurveyResult,
    urlProjectId,
    urlPartCode,
    surveyInfoResultList,
    surveyResultList,
  } = useProject();
  const { surveyInfo, surveyAddInfo, projectDetailInfo } = useProjectSurveyInfo();
  const { info } = useLogin();

  const isAddQuestion = useMemo(() => surveyAddInfo.length > 0, [surveyAddInfo.length]);

  const [isSurveyComplete, setSurveyComplete] = useState(false);
  const [isSurveyAddComplete, setSurveyAddComplete] = useState(false);

  const resultSurvey = useMemo(() => {
    if (isAddQuestion && isSurveyComplete) {
      return surveyAddInfo;
    }
    return surveyInfo;
  }, [isAddQuestion, isSurveyComplete, surveyAddInfo, surveyInfo]);

  const [isDisabled, setIsDisabled] = useState(true);
  const [openResetDialog, setOpenResetDialog] = useState(false);

  const handleSave = async () => {
    const psrObject = {
      projectId: urlProjectId || '',
      partCode: urlPartCode || '',
      surveyResultList: surveyInfoResultList,
      surveyAddResultList: surveyResultList,
    };
    dispatch(openBackdrop({}));
    await dispatch(postSurveyResult(psrObject))
      .unwrap()
      .then(response => {
        const { status } = response;
        console.log('data', response);
        if (status === 200) {
          dispatch(showMessage({ message: '진단 완료 되었습니다.', variant: 'success' }));
          setSurveyAddComplete(true);
        } else {
          dispatch(showMessage({ message: response?.data?.message || '실패했습니다.', variant: 'warning' }));
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };
  const updateProgressStep = useCallback(
    async (currentProgress: number, totalProgress: number) => {
      // 호출 조건 확인
      const isLast = currentProgress === totalProgress;
      const shouldCall =
        currentProgress <= 5 || // 5 이하는 매번 호출
        currentProgress % 5 === 0 || // 5의 배수일 때 호출
        isLast; // 마지막일 때 무조건 호출

      if (!shouldCall || !urlProjectId || !urlPartCode) {
        return;
      }

      try {
        const url = process.env.REACT_APP_URL;
        const header = makeHeader(info?.accessToken);
        const body = {
          progressStep: `${currentProgress}/${totalProgress}`,
        };
        // 비동기로 호출 (실패해도 상관없음)
        axios
          .put(`${url}/api/project/${urlProjectId}/part-code/${urlPartCode}/progress-step`, body, header)
          .catch(() => {
            // 실패해도 무시
          });
      } catch (error) {
        // 에러 무시
      }
    },
    [urlProjectId, urlPartCode, info?.accessToken],
  );

  const handleNextButton = async () => {
    const currentProgress = progressInfo + 1;
    const totalProgress = resultSurvey.length;
    console.log(`[Survey Progress] ${currentProgress}/${totalProgress}`);

    // API 호출 (비동기, 실패해도 상관없음)
    updateProgressStep(currentProgress, totalProgress);
    if (!isSurveyComplete) {
      if (progressInfo + 1 === surveyInfo.length) {
        if (!isAddQuestion) {
          await handleSave();
        } else {
          setSurveyComplete(true);
          changeProgressInfo(0);
        }
      } else {
        changeProgressInfo(progressInfo + 1);
      }
    } else if (progressInfo + 1 === surveyAddInfo.length) {
      await handleSave();
    } else {
      changeProgressInfo(progressInfo + 1);
    }
  };
  const handleNext = useCallback(
    async (_type: string, _surveyId: string, _info: any) => {
      const tmpResult: IPostSurveyResultListProps = {
        surveyId: '',
        answerResult: '',
        choiceResultList: [],
      };
      switch (_type) {
        case 'OC': // OC(oneChoice):객관식
        case 'LK': // LK: 리커트
          tmpResult.surveyId = _surveyId;
          tmpResult.choiceResultList = [_info];
          break;
        case 'MC': // MC(multiChoice):체크박스,
          tmpResult.surveyId = _surveyId;
          tmpResult.choiceResultList = _info;
          break;
        case 'DA': // DA(descriptiveAnswer):서술형
        case 'SA': // SA(shortAnswer):단답형
          tmpResult.surveyId = _surveyId;
          tmpResult.answerResult = _info;
          break;
        default:
          break;
      }
      if (!isSurveyComplete) {
        await changeSurveyInfoResult(tmpResult);
      } else {
        await changeSurveyResult(tmpResult);
      }
    },
    [changeSurveyInfoResult, changeSurveyResult, isSurveyComplete],
  );
  const handleReset = () => {
    setOpenResetDialog(false);
    window.location.reload();
  };
  const goToLast = () => {
    const firstUnansweredIdx = resultSurvey.findIndex(q => {
      const result = !isSurveyComplete
        ? surveyInfoResultList.find(r => r.surveyId === q.id)
        : surveyResultList.find(r => r.surveyId === q.id);
      if (q.questionType === 'OC' || q.questionType === 'LK' || q.questionType === 'MC') {
        return !result || !result.choiceResultList || result.choiceResultList.length === 0;
      }
      return !result || !result.answerResult;
    });

    if (firstUnansweredIdx === -1) {
      changeProgressInfo(resultSurvey.length - 1);
    } else {
      changeProgressInfo(firstUnansweredIdx);
    }
  };
  const handleTestFill = async () => {
    const promises = resultSurvey.map((q, idx) => {
      const tmpResult: IPostSurveyResultListProps = {
        surveyId: q.id,
        answerResult: '',
        choiceResultList: [],
      };
      if (q.questionType === 'OC' || q.questionType === 'LK') {
        if (q.items && q.items.length > 0) {
          tmpResult.choiceResultList = [q.items[idx % q.items.length].id];
        }
      } else if (q.questionType === 'MC') {
        if (q.items && q.items.length > 0) {
          tmpResult.choiceResultList = [q.items[idx % q.items.length].id];
        }
      } else if (q.questionType === 'DA' || q.questionType === 'SA') {
        tmpResult.answerResult = '테스트';
      }
      return !isSurveyComplete ? changeSurveyInfoResult(tmpResult) : changeSurveyResult(tmpResult);
    });
    await Promise.all(promises);
    const firstUnansweredIdx = resultSurvey.findIndex(q => {
      const result = surveyResultList.find(r => r.surveyId === q.id);
      if (q.questionType === 'OC' || q.questionType === 'LK' || q.questionType === 'MC') {
        return !result || !result.choiceResultList || result.choiceResultList.length === 0;
      }
      return !result || !result.answerResult;
    });
    changeProgressInfo(firstUnansweredIdx !== -1 ? firstUnansweredIdx : 0);
  };
  return (
    <>
      {isSurveyAddComplete || (!isAddQuestion && isSurveyComplete) ? (
        <Box sx={{ width: '100%' }}>
          <ClassificationGuideComplete title="설문/진단이 완료되었습니다." projectDetailInfo={projectDetailInfo} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box
            sx={{
              flex: 1,
              p: { xs: 0, sm: 0 },
              width: '100%',
              overflow: 'auto',
              pb: { xs: '100px!important', sm: '116px!important' },
            }}
          >
            <Container maxWidth="xl" disableGutters sx={{ height: '100%' }}>
              <Container
                maxWidth="lg"
                disableGutters
                sx={{
                  px: { xs: 0.5, sm: 1 },
                  height: '100%',
                  display: 'flex',
                  alignItems: { xs: 'center', sm: 'center' },
                  justifyContent: 'center',
                }}
              >
                <Stack
                  direction="column"
                  justifyContent={{ xs: 'center', sm: 'center' }}
                  alignContent="center"
                  alignItems="center"
                  sx={{ width: { xs: '100%', sm: '550px' } }}
                >
                  <Box
                    sx={{
                      width: { xs: '100%', sm: '550px' },
                      display: 'flex',
                      justifyContent: 'flex-start',
                      mb: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <Box component="img" src={SK_LOGO} alt="SK hynix logo" sx={{ width: 140, height: 'auto' }} />
                  </Box>
                  <Box sx={{ position: 'relative', width: { xs: '100%', sm: '550px' } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'white',
                        width: { xs: '100%', sm: '550px' },
                        minWidth: { xs: '100%', sm: '550px' },
                        maxWidth: { xs: '100%', sm: '550px' },
                        borderRadius: '12px',
                        boxShadow: '0px 0px 8px 0px #00000010',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        flex: 1,
                        height: 'auto',
                        maxHeight: { md: 'calc(100vh - 180px)' },
                        overflowY: 'auto',
                      }}
                    >
                      <Stack
                        direction="column"
                        spacing={2}
                        sx={{
                          width: { xs: '100%', sm: '500px' },
                          minWidth: { xs: '100%', sm: '500px' },
                          maxWidth: { xs: '100%', sm: '500px' },
                          p: { xs: '8px', sm: '24px' },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            mb: 2,
                            minHeight: '80px',
                          }}
                        >
                          <Typography
                            sx={{ fontSize: 16, fontWeight: 'bold', lineHeight: 1.5 }}
                            color="text.primary"
                            gutterBottom
                          >
                            Q{progressInfo + 1}.
                            <br />
                            {resultSurvey[progressInfo]?.question || ''}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }} />
                        <ProgressIndicator current={progressInfo + 1} total={resultSurvey.length ?? 0} />
                        {resultSurvey[progressInfo]?.questionType === 'OC' && (
                          <SurveyRadioBox
                            id={resultSurvey[progressInfo].id}
                            items={resultSurvey[progressInfo].items}
                            type={resultSurvey[progressInfo].questionType}
                            handleNext={handleNext}
                            setIsDisabled={setIsDisabled}
                            surveyType="survey"
                            // eslint-disable-next-line prettier/prettier
                            handleNextButton={!isSurveyComplete && progressInfo < resultSurvey.length - 1 ? handleNextButton : undefined}
                          />
                        )}
                        {resultSurvey[progressInfo]?.questionType === 'SA' && (
                          <SurveyShortAnswer
                            id={resultSurvey[progressInfo].id}
                            type={resultSurvey[progressInfo].questionType}
                            handleNext={handleNext}
                            setIsDisabled={setIsDisabled}
                            surveyType="survey"
                          />
                        )}
                        {resultSurvey[progressInfo]?.questionType === 'MC' && (
                          <SurveyMultiChoice
                            id={resultSurvey[progressInfo].id}
                            items={resultSurvey[progressInfo]?.items}
                            type={resultSurvey[progressInfo]?.questionType}
                            handleNext={handleNext}
                            setIsDisabled={setIsDisabled}
                          />
                        )}
                        {resultSurvey[progressInfo]?.questionType === 'DA' && (
                          <SurveyDescriptionAnswer
                            id={resultSurvey[progressInfo].id}
                            type={resultSurvey[progressInfo].questionType}
                            handleNext={handleNext}
                            setIsDisabled={setIsDisabled}
                            surveyType="survey"
                          />
                        )}
                        {resultSurvey[progressInfo]?.questionType === 'LK' && (
                          <SurveyLK
                            id={resultSurvey[progressInfo].id}
                            items={resultSurvey[progressInfo].items}
                            type={resultSurvey[progressInfo].questionType}
                            handleNext={handleNext}
                            setIsDisabled={setIsDisabled}
                            surveyType="survey"
                            questionOrderOneBased={progressInfo + 1}
                            surveyQuestionTotal={resultSurvey.length}
                            // eslint-disable-next-line prettier/prettier
                            handleNextButton={!isSurveyComplete && progressInfo < resultSurvey.length - 1 ? handleNextButton : undefined}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              </Container>
            </Container>
          </Box>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100%',
              bgcolor: 'background.paper',
              boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
              py: 2,
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => changeProgressInfo(progressInfo - 1)}
              disabled={progressInfo === 0}
              sx={{ minWidth: 64, display: 'none' }}
            >
              이전
            </Button>
            <PositiveButton
              variant="contained"
              onClick={handleNextButton}
              sx={{ width: { xs: 130 }, height: 52, typography: 's14w500' }}
              title={progressInfo === resultSurvey.length - 1 ? '완료' : '다음'}
              disabled={isDisabled}
            />
            <Button variant="outlined" color="primary" onClick={goToLast} sx={{ minWidth: 34, display: 'none' }}>
              마지막
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleTestFill}
              sx={{ minWidth: 34, display: 'none' }}
            >
              자동
            </Button>
          </Box>
        </Box>
      )}
      {openResetDialog && (
        <ConfirmDialog
          title="초기화 확인"
          contents="정말 초기화하시겠습니까? 모든 답변이 삭제됩니다."
          handleClick={handleReset}
          buttonText="초기화"
        />
      )}
    </>
  );
}

export default memo(SurveyPage);
